/**
 * On the stage, above the answer: when the search exactly names a system or an integration ("ERP", "@system=ERP",
 * "[ERP] Sales Orders to WMS", @process="…"), a one-line card offers its page — "ERP — system · 5 integrations · 1 failing
 * → Open". Additive: the search's own answer stays as it is. The integrations of the week are asked once a minute at
 * most (the cloud keeps them five minutes), and only for a search that could name something.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Api, type Integration } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useEnv } from '../lib/envs.tsx';
import { count } from '../lib/format.ts';
import { integrationPath, namedThing, systemCounts, systemPath, type Named } from '../lib/systemsview.ts';
import { registeredSystems, stripSystem, systemName } from '../lib/systems.ts';
import { SystemBadge, cx } from '../design/primitives.tsx';
import { ArrowIcon } from '../components/icons.tsx';
import { Pair, StatusMark } from './parts.tsx';

let kept: { key: string; at: number; list: Promise<Integration[]> } | null = null;
function weekOf(tid: string, env: string): Promise<Integration[]> {
  const key = `${tid}|${env}`;
  if (kept && kept.key === key && Date.now() - kept.at < 60_000) return kept.list;
  const list = Api.integrations(tid, new URLSearchParams({ from: 'now-7d' })).then((r) => r.integrations);
  kept = { key, at: Date.now(), list };
  list.catch(() => { kept = null; });
  return list;
}

/** A search that could be a name: short, one phrase, or @system= / @process= alone. */
const couldName = (q: string) => {
  const t = q.trim();
  return t.length > 0 && t.length <= 160 && (/^@(system|process)[=:]/i.test(t) || (!t.startsWith('@') && !/[=<>()]/.test(t) && !/\s(OR|AND)\s/.test(t)));
};

export function NamedCard({ q }: { q: string }) {
  const { tenant } = useAuth();
  const { link, current } = useEnv();
  const tid = tenant?.id ?? '';
  const [list, setList] = useState<{ key: string; items: Integration[] } | null>(null);
  const key = `${tid}|${current?.id ?? ''}`;
  const wanted = couldName(q);
  useEffect(() => {
    if (!wanted || !tid) return;
    let cancelled = false;
    weekOf(tid, current?.id ?? '').then((items) => { if (!cancelled) setList({ key, items }); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [wanted, tid, key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!wanted) return null;
  const items = list?.key === key ? list.items : null;
  const systems = [...registeredSystems().values()];
  const named: Named | null = namedThing(q, systems, items ? [...new Set(items.map((i) => i.process))] : []);
  if (!named) return null;

  if (named.kind === 'system') {
    const c = items ? systemCounts(items, named.code) : null;
    return (
      <Card to={link(systemPath(named.code))} label={`Open the system ${systemName(named.code)}`} data={{ 'data-named-system': named.code }}>
        <SystemBadge code={named.code} size={24} />
        <span className="font-semibold text-n10 truncate">{systemName(named.code)}</span>
        <span className="text-n8">— system</span>
        {c && <span className="text-n8 tabular-nums">· {count(c.integrations)} {c.integrations === 1 ? 'integration' : 'integrations'}</span>}
        {c && c.failing > 0 && <span className="text-fail tabular-nums">· {count(c.failing)} failing</span>}
        {c && c.overdue > 0 && <span className="text-warn tabular-nums">· {count(c.overdue)} overdue</span>}
      </Card>
    );
  }
  const rows = items?.filter((i) => i.process === named.process) ?? [];
  const status = rows.find((r) => r.status === 'failing')?.status ?? rows[0]?.status ?? null;
  return (
    <Card to={link(integrationPath(named.process))} label={`Open the integration ${named.process}`} data={{ 'data-named-integration': named.process }}>
      <span className="font-semibold text-n10 truncate">{stripSystem(named.process)}</span>
      <span className="text-n8">— integration</span>
      {rows.map((r) => <Pair key={`${r.from}>${r.to}`} from={r.from} to={r.to} />)}
      {status && <StatusMark status={status} />}
    </Card>
  );
}

function Card({ to, label, data, children }: { to: string; label: string; data: Record<string, string>; children: React.ReactNode }) {
  return (
    <Link to={to} aria-label={label} {...data}
      className={cx('group flex items-center gap-2.5 min-w-0 mb-3 px-4 h-12 rounded-card bg-n2 ring-1 ring-inset ring-n4 no-underline text-small',
        'transition-shadow duration-[var(--t-micro)] hover:ring-n6 hover:shadow-node mv-rise')}>
      {children}
      <span className="ml-auto inline-flex items-center gap-1.5 text-small font-semibold text-n10 shrink-0">Open<ArrowIcon size={15} className="transition-transform duration-[var(--t-micro)] group-hover:translate-x-0.5" /></span>
    </Link>
  );
}
